import React, { useState } from 'react';
import AltaSignage from './documentation/AltaSignage.pdf';
import PriceEat from './documentation/PriceEatDoc.pdf';
import {
    DocumentArrowDownIcon,
    TvIcon,
    CurrencyEuroIcon,
    ChevronDownIcon,
    Squares2X2Icon,
    PlayCircleIcon,
    CalendarDaysIcon,
    VideoCameraIcon,
    BellIcon,
    ClockIcon,
    RectangleGroupIcon,
    LightBulbIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------- PDFs téléchargeables (existants)
const DOCS = [
    {
        title: 'Créer une application Alta Signage',
        subtitle: 'Guide de provisioning des écrans (PDF)',
        href: AltaSignage,
        download: 'AltaSignage',
        icon: TvIcon,
        wrap: 'bg-indigo-500/10 text-indigo-600',
    },
    {
        title: 'Créer un Price-Eat',
        subtitle: 'Configuration des prix et articles (PDF)',
        href: PriceEat,
        download: 'PriceEatDoc',
        icon: CurrencyEuroIcon,
        wrap: 'bg-emerald-500/10 text-emerald-600',
    },
];

// ---------------------------------------------------------------- Guides d'utilisation (formation)
// Chaque guide = étapes concrètes + astuces. Rédigé pour un nouvel utilisateur de la console.
const GUIDES = [
    {
        id: 'dashboard',
        icon: Squares2X2Icon,
        wrap: 'bg-indigo-500/10 text-indigo-600',
        title: 'Prendre en main le Dashboard',
        summary: "Vue d'ensemble du parc dès la connexion.",
        steps: [
            "Les 4 compteurs du haut donnent l'état du parc en temps réel : En diffusion (vert), Synchronisation (ambre), À l'arrêt (gris), Hors ligne (rouge). Cliquez sur un compteur pour ouvrir le détail Now Playing.",
            "Le panneau « En diffusion maintenant » montre les 5 écrans actifs avec le clip en cours et sa progression.",
            "« Derniers déploiements » liste les 5 dernières publications de contenu avec leur statut (Published / In Progress / Failed).",
            "La pastille en haut à droite du bandeau (chip verte/ambre/rouge) reste visible sur TOUTES les pages : c'est votre témoin de santé du parc.",
        ],
        tip: "Un écran « À l'arrêt » est en ligne (heartbeat récent) mais ne diffuse pas : vérifiez son programme ou sa plage horaire avant de suspecter une panne.",
    },
    {
        id: 'screens',
        icon: TvIcon,
        wrap: 'bg-blue-500/10 text-blue-600',
        title: 'Gérer les écrans (approbation & suivi)',
        summary: "Approuver un nouvel écran et suivre le parc.",
        steps: [
            "Un nouvel écran installé apparaît dans « En attente d'approbation ». Cliquez sur sa ligne pour ouvrir la fiche.",
            "Dans la fiche : renseignez le nom client, le type d'app, la date de fin (saisissable au clavier ou au calendrier), puis passez « Autorisé » à Oui et validez.",
            "Le champ Client ID dispose d'une recherche (loupe) vers les comptes Price-Eat ; le champ Room ID a la même recherche vers les clients Call-Eat.",
            "Dans « Écrans approuvés » : filtrez par statut de connexion (En ligne, < 24h, < 7 jours…), par type d'app (bouton Filtres), choisissez vos colonnes (bouton Colonnes) et exportez en Excel.",
            "La recherche accepte : nom, PIN, id_pdv, id call-eat, nom de vidéo ou de programme.",
        ],
        tip: "Le badge de statut se base sur le dernier heartbeat (« En ligne » = vu il y a moins de 5 minutes).",
    },
    {
        id: 'nowplaying',
        icon: PlayCircleIcon,
        wrap: 'bg-emerald-500/10 text-emerald-600',
        title: 'Superviser la diffusion (Now Playing)',
        summary: "Qui joue quoi, en ce moment, sur tout le parc.",
        steps: [
            "Chaque carte = un écran : clip en cours, programme, barre de progression animée et plateforme (Android, Tizen, webOS, Electron).",
            "Pastilles : EN DIRECT (vert) = l'écran émet son état de lecture ; SYNC (ambre) = téléchargement de contenu en cours ; À L'ARRÊT (gris) = en ligne sans diffusion ; HORS LIGNE (rouge) = plus aucun signal.",
            "La case « hors ligne » en haut à droite permet d'afficher/masquer les écrans hors ligne.",
            "Un écran passe automatiquement HORS LIGNE au bout de 15 s sans nouvelles de sa lecture — croisé avec le heartbeat réseau pour distinguer « éteint » de « à l'arrêt ».",
        ],
        tip: "La barre de progression avance en continu entre deux remontées (interpolation locale) : une barre figée plusieurs secondes = écran qui n'émet plus.",
    },
    {
        id: 'programmes',
        icon: CalendarDaysIcon,
        wrap: 'bg-violet-500/10 text-violet-600',
        title: 'Programmer du contenu',
        summary: "Créer et planifier la diffusion des vidéos.",
        steps: [
            "Diffusion → Programmes : « Vue cartes » pour naviguer par client, « Vue liste » pour tout voir d'un coup.",
            "Ouvrez le planning d'un écran depuis sa fiche (« Écrans approuvés » → ligne → Programme).",
            "Dans le planificateur : créez un créneau, associez jusqu'à 5 vidéos (loupe = recherche dans la médiathèque), définissez récurrence et plage horaire.",
            "« Copier un programme » duplique la planification d'un écran vers un autre.",
            "Après enregistrement, le contenu part automatiquement vers l'écran : suivez l'avancement dans Historique déploiements.",
        ],
        tip: "Publiez les gros médias en heures creuses : l'écran continue de diffuser l'ancien programme tant que le nouveau n'est pas téléchargé à 100 %.",
    },
    {
        id: 'media',
        icon: VideoCameraIcon,
        wrap: 'bg-sky-500/10 text-sky-600',
        title: 'Utiliser la médiathèque',
        summary: "Uploader, organiser et purger les vidéos.",
        steps: [
            "Contenu → Médiathèque : naviguez dans les dossiers (fil d'Ariane cliquable pour remonter), triez par nom, date ou taille.",
            "Cliquez sur un fichier pour le prévisualiser ; la corbeille en bout de ligne supprime (avec confirmation).",
            "« Uploader » envoie un nouveau média ; il devient immédiatement disponible dans le planificateur.",
            "« Purger les inutilisées » supprime les vidéos qui ne sont référencées par aucun programme.",
            "La jauge de stockage passe à l'orange au-delà de 75 % et au rouge au-delà de 90 %.",
        ],
        tip: "Encodez en H.264 (MP4) 1080p pour une compatibilité totale du parc — réservez HEVC/4K aux écrans validés pour ça.",
    },
    {
        id: 'priceeat',
        icon: CurrencyEuroIcon,
        wrap: 'bg-emerald-500/10 text-emerald-600',
        title: 'Price-Eat : placer des prix sur une vidéo',
        summary: "L'éditeur visuel WYSIWYG des prix.",
        steps: [
            "Métiers → Price-Eat → Articles & prix : choisissez le point de vente, puis ouvrez l'éditeur.",
            "Cliquez N'IMPORTE QUEL prix directement sur la vidéo pour le sélectionner, glissez-le pour le déplacer, « Enregistrer » pour confirmer — pas besoin de passer par la liste.",
            "Taille de police au curseur (+/-), couleur avec alerte de lisibilité, polices synchronisées avec les players.",
            "« Aperçu réel » masque les repères d'édition pour voir le rendu exact écran.",
            "Ctrl+Z / Ctrl+Y : annuler / rétablir. Flèches clavier : déplacement fin du prix sélectionné.",
            "« Appliquer le style à tous » propage police/taille/couleur à tous les prix de l'écran.",
        ],
        tip: "La colonne « Dernière connexion » de Gestion Utilisateurs repère les comptes dormants ; « Non utilisé » = id_pdv absent de tout programme et device → candidat au nettoyage.",
    },
    {
        id: 'calleat',
        icon: BellIcon,
        wrap: 'bg-amber-500/10 text-amber-600',
        title: 'Call-Eat : clients et rattachement',
        summary: "Gérer les bornes d'appel et leurs rooms.",
        steps: [
            "Métiers → Call-Eat : chaque client possède un Room ID (identifiant de salle) et un statut Autorisé.",
            "La colonne « Activité » indique si la room a déjà reçu des commandes et la date de la dernière (badge « Jamais utilisé » = candidat au nettoyage). Filtre « Non utilisés uniquement » disponible.",
            "Pour rattacher un écran à un Call-Eat : fiche de l'écran → champ Room ID → loupe → recherchez le client par nom ou room id.",
        ],
        tip: "Un Room ID saisi à la main est source de fautes de frappe : passez toujours par la recherche intégrée.",
    },
    {
        id: 'deploy',
        icon: ClockIcon,
        wrap: 'bg-rose-500/10 text-rose-600',
        title: 'Suivre les déploiements',
        summary: "Vérifier que le contenu est bien arrivé sur les écrans.",
        steps: [
            "Diffusion → Historique déploiements : chaque session = une publication de contenu vers un écran.",
            "Statuts : Published (tout reçu), Published (Cached) (réutilisation du cache local), In Progress, Failed.",
            "Dépliez une ligne pour le détail fichier par fichier : progression, tentatives, cause d'échec.",
            "Filtres par période, statut, écran ; export CSV pour le reporting.",
            "Les écrans bloqués ou en fallback cache remontent aussi en tête de « Écrans approuvés » (encart Suivi Download Fleet).",
        ],
        tip: "« Failed » avec plusieurs tentatives = souvent un problème réseau local du site ; vérifiez le statut de connexion de l'écran avant de republier.",
    },
    {
        id: 'videowall',
        icon: RectangleGroupIcon,
        wrap: 'bg-cyan-500/10 text-cyan-600',
        title: "Murs d'écrans (videowall)",
        summary: "Synchroniser plusieurs écrans sur un même visuel.",
        steps: [
            "Écrans → Murs d'écrans : un mur = un écran « serveur » (maître) et des écrans « clients » synchronisés.",
            "Créez le mur, associez les PIN clients au PIN serveur, nommez le mur pour le retrouver facilement.",
            "Chaque écran du mur lit sa découpe (TL/TR/BL/BR en 2x2) : les vidéos doivent être découpées en amont avec la MÊME durée et le même nombre d'images par tranche.",
            "Les prix Price-Eat d'un mur se positionnent dans l'éditeur avec le recadrage par quadrant (bouton crop 4K).",
        ],
        tip: "Si un mur se désynchronise sur UN visuel précis, vérifiez d'abord le contenu (durées des tranches) avant de suspecter le réseau.",
    },
];

function GuideCard({ guide, open, onToggle }) {
    const Icon = guide.icon;
    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center gap-4 px-5 py-4 text-left transition hover:bg-slate-50"
            >
                <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${guide.wrap}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-slate-800">{guide.title}</h3>
                    <p className="text-xs text-slate-400">{guide.summary}</p>
                </div>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="border-t border-slate-100 px-5 py-4">
                    <ol className="space-y-2.5">
                        {guide.steps.map((step, i) => (
                            <li key={i} className="flex gap-3 text-sm text-slate-600">
                                <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-600">
                                    {i + 1}
                                </span>
                                <span>{step}</span>
                            </li>
                        ))}
                    </ol>
                    {guide.tip && (
                        <div className="mt-4 flex gap-2.5 rounded-xl bg-amber-50 px-3.5 py-3 text-sm text-amber-800 ring-1 ring-amber-100">
                            <LightBulbIcon className="h-5 w-5 shrink-0 text-amber-500" />
                            <span><span className="font-semibold">Astuce :</span> {guide.tip}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Documentation() {
    const [openId, setOpenId] = useState(null);

    return (
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-slate-800">Documentation</h1>
                <p className="text-sm text-slate-500">Guides d'utilisation, formation et documents PDF.</p>
            </div>

            {/* Guides interactifs (formation) */}
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">Guides d'utilisation</h2>
            <div className="mb-8 grid grid-cols-1 gap-3 lg:grid-cols-2">
                {GUIDES.map((g) => (
                    <GuideCard
                        key={g.id}
                        guide={g}
                        open={openId === g.id}
                        onToggle={() => setOpenId(openId === g.id ? null : g.id)}
                    />
                ))}
            </div>

            {/* PDFs téléchargeables */}
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">Documents PDF</h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {DOCS.map((doc) => {
                    const Icon = doc.icon;
                    return (
                        <a
                            key={doc.title}
                            href={doc.href}
                            download={doc.download}
                            target="_blank"
                            rel="noreferrer"
                            className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg"
                        >
                            <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl ${doc.wrap}`}>
                                <Icon className="h-6 w-6" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold text-slate-800">{doc.title}</h3>
                                <p className="mt-0.5 text-xs text-slate-400">{doc.subtitle}</p>
                            </div>
                            <DocumentArrowDownIcon className="h-5 w-5 shrink-0 text-slate-300 transition group-hover:text-indigo-500" />
                        </a>
                    );
                })}
            </div>
        </div>
    );
}

export default Documentation;
